import { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDelayedState } from '@nexelem/utils';
import { DndQueuesSettingsContext } from './DndQueuesSettingsContext';
import { QueueGroupContainer } from './QueueGroupContainer';
import { memo, useCallback } from 'react';
import emptyState from '../../../../../../../assets/empty-state.svg';
import { Button } from '@nexelem/components';
import { useCustomTranslation } from '../../../../../../hooks';
import { useGroupedQueuesStore, QueueGroup } from '../../../store';
import { SearchChip } from '@nexelem/inputs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/pro-light-svg-icons';

export const SettingsQueueGroupList = () => {
  const { tWrapper } = useCustomTranslation('common');

  const [search, delayedSearch, setSearch] = useDelayedState('');

  const clearSearch = useCallback(() => {
    setSearch('');
  }, [setSearch]);

  return (
    <>
      <div className="my-2 px-4">
        <SearchChip placeholder={tWrapper('search')} value={search} onChange={setSearch} />
      </div>
      <ListRenderer clearSearch={clearSearch} search={delayedSearch} />
    </>
  );
};

type ListRendererProps = {
  search: string;
  clearSearch: () => void;
};

const ListRenderer = memo(({ search, clearSearch }: ListRendererProps) => {
  const { tWrapper } = useCustomTranslation('common');
  const queueGroups = useGroupedQueuesStore((state) => state.draftGroups);
  const moveGroup = useGroupedQueuesStore((state) => state.moveGroup);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const activeId = active.id as number;
    const overId = over?.id as number;

    if (activeId !== overId) {
      moveGroup(activeId, overId);
    }
  };

  const filteredQueueGroups = searchFilter(queueGroups, search);

  return filteredQueueGroups.length ? (
    <div className="mt-2 flex flex-col border-b border-b-border-container">
      <DndQueuesSettingsContext handleDragEnd={handleDragEnd}>
        <SortableContext items={queueGroups} strategy={verticalListSortingStrategy}>
          {filteredQueueGroups.map((queueGroup) => (
            <QueueGroupContainer forceOpen={!!search} key={queueGroup.id} queueGroup={queueGroup} />
          ))}
        </SortableContext>
      </DndQueuesSettingsContext>
    </div>
  ) : (
    <div className="mt-20 flex flex-col items-center justify-center gap-4">
      <div className="h-70 w-70 bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url(${emptyState})` }} />
      <span className="text-s18">{tWrapper('noResultsFound')}</span>
      <Button StartIcon={<FontAwesomeIcon icon={faCircleXmark} />} variant="outlined" onClick={clearSearch}>
        {tWrapper('clearSearch')}
      </Button>
    </div>
  );
});

ListRenderer.displayName = 'ListRenderer';

const searchFilter = (queueGroups: QueueGroup[], search: string): QueueGroup[] => {
  if (search) {
    return queueGroups
      .map((queueGroup) => {
        return {
          ...queueGroup,
          queues: queueGroup.queues.filter((queue) => {
            const searchIn = (queue.name + queue.shortName).toLowerCase();
            return searchIn.includes(search.toLowerCase());
          }),
        };
      })
      .filter((queueGroup) => queueGroup.queues.length);
  }

  return queueGroups;
};
